import { BidderPaymentStateSchema, InvoicePaymentStatusSchema } from '@neon-one/auctions-types';
import type { BidderItemSummary, BidderPaymentState, InvoicePaymentStatus } from '@neon-one/auctions-types';

/**
 * The four things an invoice can be, ranked by how much it still wants from
 * the bidder. Every surface that asks "is this invoice open / settled" reads
 * this one map, so no screen can invent its own status list and drift from
 * the wire's (frontend-structure.md §4.8).
 */
const TRACK_RANK_TIER = {
  /** Money is owed and she is the one who has to move. */
  open: 3,
  /** Money is in flight — worth showing, but an open invoice outranks it. */
  inFlight: 2,
  /** Money has been collected. Kept so a settled bidder still reaches a receipt. */
  settled: 1,
  /** ADR-012 §4 — nothing collected, nothing owed, every lot left. Terminal. */
  dead: 0,
} as const;

/**
 * How interesting an invoice is to the bidder looking at My Wins.
 *
 * A bidder can hold more than one invoice per auction (PRD invariant 2: at
 * most one of them is *open*). Tracking chooses the checkout or receipt
 * destination, not the event-wide money totals. An invoice needing action
 * outranks a receipt already paid.
 *
 * Higher wins. Ties keep payload order, which keeps the choice stable.
 */
const TRACK_RANK: Record<InvoicePaymentStatus, number> = {
  pending: TRACK_RANK_TIER.open,
  failed: TRACK_RANK_TIER.open,
  processing: TRACK_RANK_TIER.inFlight,
  paid: TRACK_RANK_TIER.settled,
  refunded: TRACK_RANK_TIER.settled,
  partially_refunded: TRACK_RANK_TIER.settled,
  void: TRACK_RANK_TIER.dead,
};

/**
 * Rows from older payloads can carry an invoice with no status yet. The store
 * has always treated those as 'pending', so they rank as open.
 */
const UNKNOWN_STATUS_RANK = TRACK_RANK.pending;

export function invoiceTrackRank(status: InvoicePaymentStatus | null | undefined): number {
  if (status == null) return UNKNOWN_STATUS_RANK;
  return TRACK_RANK[status] ?? UNKNOWN_STATUS_RANK;
}

/**
 * Can this invoice still take another line — an accepted offer, a reassigned
 * lot — and then a charge?
 *
 * Deliberately a POSITIVE set (`pending` / `failed`) rather than "not
 * settled". Written as a negation, every status the wire has ever grown
 * silently counted as open: `void` did, which had My Wins promise a bidder
 * that accepting an offer would join an invoice that was terminated and
 * collecting nothing. A status this function has not been taught is not open.
 */
export function isInvoiceOpen(status: InvoicePaymentStatus | null | undefined): boolean {
  return status != null && TRACK_RANK[status] === TRACK_RANK_TIER.open;
}

/**
 * Nothing left for the bidder to pay, so the invoice routes to its receipt rather than
 * checkout (GT1-346): paid, refunded or partially refunded (paid before it was returned),
 * void (nothing collected, every lot released), or a submitted payment still clearing.
 * A processing charge Neon Pay has not answered, or a terminal tap, is not settled.
 */
export function isInvoiceSettled(status: InvoicePaymentStatus | null | undefined,
  paymentState?: BidderPaymentState): boolean {
  return status != null && (TRACK_RANK[status] <= TRACK_RANK_TIER.settled
    || (status === InvoicePaymentStatusSchema.enum.processing && paymentState === BidderPaymentStateSchema.enum.clearing));
}

/**
 * The won row whose invoice My Wins should follow: the bidder's open invoice
 * when she has one, otherwise the most interesting settled invoice so the
 * page still resolves to a receipt rather than to nothing.
 */
export function pickTrackedInvoiceItem(
  wonItems: readonly BidderItemSummary[],
): BidderItemSummary | null {
  let best: BidderItemSummary | null = null;
  let bestRank = -1;

  for (const item of wonItems) {
    if (item.invoiceUuid == null) continue;
    const rank = invoiceTrackRank(item.invoicePaymentStatus);
    if (rank > bestRank) {
      best = item;
      bestRank = rank;
    }
  }

  return best;
}
